import { isAnyOf } from '@reduxjs/toolkit';
import type { AppStartListening } from '@/app/listeners';
import { sessionRestored, sessionStarted } from '@/shared/session';
import { portalApi } from '../api/portalApi';
import type { ListEntity } from '../api/types';
import { billingSet, compareSet, offerSet, rangeSet, unitSet } from './filtersSlice';
import { prefsChanged } from './prefsSlice';
import { STORAGE_KEYS, writeJson } from './storage';
import { chartsToggled, navToggled, toastExpired, toastShown } from './uiSlice';
import {
  customViewCreated,
  customViewDeleted,
  customViewRenamed,
  customViewSaved,
  viewSelected,
} from './viewsSlice';

const TOAST_MS = 2600;

const LIST_ENTITIES: ListEntity[] = ['subscriptions', 'users', 'apps', 'leads'];

/**
 * Persistence and timers of the portal state (filters, preferences, menu, hidden charts, custom
 * views, toasts),
 * plus the portal data cache: reference data is prefetched with the session, and cached data is
 * invalidated when the backend publishes a new snapshot.
 */
export function registerPortalListeners(startListening: AppStartListening): void {
  // Pages wait for `meta` (and lists for their fields) before requesting data; start these requests
  // with the session, in parallel with loading the page's code.
  startListening({
    matcher: isAnyOf(sessionStarted, sessionRestored),
    effect: (_action, api) => {
      api.dispatch(portalApi.util.prefetch('getMeta', undefined, {}));
      api.dispatch(portalApi.util.prefetch('getCatalog', undefined, {}));
      for (const entity of LIST_ENTITIES) {
        api.dispatch(portalApi.util.prefetch('getFields', entity, {}));
      }
    },
  });

  startListening({
    matcher: portalApi.endpoints.getMeta.matchFulfilled,
    effect: (action, api) => {
      const previous = portalApi.endpoints.getMeta.select(undefined)(api.getOriginalState()).data;
      if (previous && previous.generatedAt !== action.payload.generatedAt) {
        api.dispatch(portalApi.util.invalidateTags(['Portal']));
      }
    },
  });

  startListening({
    matcher: isAnyOf(rangeSet, compareSet, unitSet, offerSet, billingSet, prefsChanged),
    effect: (_action, api) => {
      writeJson(STORAGE_KEYS.filters, api.getState().filters);
    },
  });

  startListening({
    actionCreator: prefsChanged,
    effect: (_action, api) => {
      writeJson(STORAGE_KEYS.prefs, api.getState().prefs);
    },
  });

  startListening({
    actionCreator: navToggled,
    effect: (_action, api) => {
      writeJson(STORAGE_KEYS.nav, api.getState().ui.navExpanded);
    },
  });

  startListening({
    actionCreator: chartsToggled,
    effect: (_action, api) => {
      writeJson(STORAGE_KEYS.charts, api.getState().ui.chartsHidden);
    },
  });

  const changesSavedViews = isAnyOf(
    viewSelected,
    customViewCreated,
    customViewSaved,
    customViewRenamed,
    customViewDeleted,
  );
  startListening({
    matcher: changesSavedViews,
    effect: (action, api) => {
      if (!changesSavedViews(action)) return;
      const { entity } = action.payload;
      const list = api.getState().views[entity];
      writeJson(STORAGE_KEYS.views(entity), { custom: list.custom, active: list.activeId });
    },
  });

  startListening({
    actionCreator: toastShown,
    effect: async (action, api) => {
      await api.delay(TOAST_MS);
      api.dispatch(toastExpired(action.payload.id));
    },
  });
}
